import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldCheck, Plus, Upload, Clock, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";
import {
  listCredentialTypes, listMyCredentials, listOrgCredentials,
  createCredential, uploadCredentialFile, statusTone, daysUntil,
  type Credential, type CredentialType,
} from "@/lib/credentials";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/credentials/")({
  component: CredentialsPage,
  head: () => ({ meta: [{ title: "Credentials — MissionReady" }] }),
});

function CredentialsPage() {
  const { currentOrg, user, currentRole } = useAuth();
  const orgId = currentOrg?.id;
  const userId = user?.id;
  const isStaff = ["org_owner", "admin", "supervisor", "dispatcher"].includes(currentRole ?? "");

  const [mine, setMine] = useState<Credential[]>([]);
  const [org, setOrg] = useState<Credential[]>([]);
  const [types, setTypes] = useState<CredentialType[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const refresh = async () => {
    if (!orgId || !userId) return;
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        listMyCredentials(orgId, userId),
        listCredentialTypes(orgId),
      ]);
      setMine(m);
      setTypes(t);
      if (isStaff) setOrg(await listOrgCredentials(orgId));
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to load credentials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [orgId, userId]);

  // Realtime: refresh on any change to credentials in this org
  useEffect(() => {
    if (!orgId) return;
    const ch = supabase
      .channel(`credentials:${orgId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "credentials", filter: `organization_id=eq.${orgId}` }, () => refresh())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line
  }, [orgId]);

  if (!orgId || !userId) {
    return <div className="p-6 text-sm text-muted-foreground">Select an organization to view credentials.</div>;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 md:p-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">
            <ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />
            Credentials
          </h1>
          <p className="text-sm text-muted-foreground">
            Upload, track, and verify licenses & certifications. Upload-based first; OCR & state lookups when supported.
          </p>
        </div>
        <NewCredentialDialog
          open={open}
          onOpenChange={setOpen}
          orgId={orgId}
          userId={userId}
          types={types}
          onCreated={refresh}
        />
      </header>

      <Tabs defaultValue="mine" className="w-full">
        <TabsList>
          <TabsTrigger value="mine">My credentials ({mine.length})</TabsTrigger>
          {isStaff && <TabsTrigger value="org">Organization ({org.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="mine" className="mt-4">
          <CredentialList items={mine} loading={loading} emptyHint="Add your first credential to get started." />
        </TabsContent>

        {isStaff && (
          <TabsContent value="org" className="mt-4">
            <CredentialList items={org} loading={loading} showHolder emptyHint="No credentials uploaded yet." />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function CredentialList({
  items, loading, emptyHint, showHolder,
}: { items: Credential[]; loading: boolean; emptyHint: string; showHolder?: boolean }) {
  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!items.length) return (
    <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
      {emptyHint}
    </div>
  );

  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {items.map((c) => {
        const tone = statusTone(c.status);
        const days = daysUntil(c.expires_at);
        const expiringSoon = days !== null && days <= 30 && days >= 0;
        return (
          <li key={c.id}>
            <Link
              to="/credentials/$credentialId"
              params={{ credentialId: c.id }}
              className="block rounded-xl border border-border bg-card p-4 hover:border-primary/40"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">
                    {c.credential_types?.name ?? "Credential"}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {c.credential_types?.issuing_authority || c.issuing_authority || "—"}
                    {c.jurisdiction ? ` · ${c.jurisdiction}` : ""}
                  </div>
                  {showHolder && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Holder: {c.holder_full_name ?? c.user_id.slice(0, 8)}
                    </div>
                  )}
                </div>
                <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${tone.className}`}>
                  {tone.label}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {c.credential_number && <span>#{c.credential_number}</span>}
                {c.expires_at && (
                  <span className={expiringSoon ? "text-amber-400" : ""}>
                    <Clock className="mr-1 inline h-3 w-3" />
                    Expires {new Date(c.expires_at).toLocaleDateString()}
                    {days !== null && days >= 0 && ` (${days}d)`}
                    {days !== null && days < 0 && " (overdue)"}
                  </span>
                )}
                {c.awaiting_hard_card && (
                  <span className="text-amber-400">
                    <AlertTriangle className="mr-1 inline h-3 w-3" />
                    Awaiting hard card
                  </span>
                )}
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function NewCredentialDialog({
  open, onOpenChange, orgId, userId, types, onCreated,
}: {
  open: boolean; onOpenChange: (b: boolean) => void;
  orgId: string; userId: string; types: CredentialType[]; onCreated: () => void;
}) {
  const [typeId, setTypeId] = useState<string>("");
  const [number, setNumber] = useState("");
  const [holderName, setHolderName] = useState("");
  const [holderDob, setHolderDob] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [issuedAt, setIssuedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [awaitingHardCard, setAwaitingHardCard] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const selectedType = useMemo(() => types.find((t) => t.id === typeId), [typeId, types]);

  const reset = () => {
    setTypeId(""); setNumber(""); setHolderName(""); setHolderDob("");
    setJurisdiction(""); setIssuedAt(""); setExpiresAt("");
    setAwaitingHardCard(false); setFiles([]);
  };

  const submit = async () => {
    if (!typeId) return toast.error("Select a credential type");
    setBusy(true);
    try {
      const cred = await createCredential({
        organization_id: orgId,
        user_id: userId,
        credential_type_id: typeId,
        credential_number: number || null,
        holder_full_name: holderName || null,
        holder_dob: holderDob || null,
        jurisdiction: jurisdiction || selectedType?.jurisdiction || null,
        issuing_authority: selectedType?.issuing_authority || null,
        issued_at: issuedAt || null,
        expires_at: expiresAt || null,
        awaiting_hard_card: awaitingHardCard,
      });

      for (const f of files) {
        if (f.size > 25 * 1024 * 1024) {
          toast.error(`${f.name} exceeds 25MB`); continue;
        }
        await uploadCredentialFile({ credential: cred, file: f });
      }

      toast.success("Credential submitted for review");
      reset();
      onOpenChange(false);
      onCreated();
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to create credential");
    } finally {
      setBusy(false);
    }
  };

  // Group types by category for the picker
  const grouped = useMemo(() => {
    const m = new Map<string, CredentialType[]>();
    for (const t of types) {
      const list = m.get(t.category) ?? [];
      list.push(t);
      m.set(t.category, list);
    }
    return Array.from(m.entries());
  }, [types]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button><Plus className="h-4 w-4" /> Add credential</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add credential</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Credential type</Label>
            <Select value={typeId} onValueChange={setTypeId}>
              <SelectTrigger aria-label="Credential type"><SelectValue placeholder="Select credential type" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {grouped.map(([cat, list]) => (
                  <div key={cat}>
                    <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">{cat}</div>
                    {list.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}{t.jurisdiction ? ` · ${t.jurisdiction}` : ""}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedType?.requires_credential_number && (
            <div className="space-y-1.5">
              <Label>Credential / license number</Label>
              <Input aria-label="Credential / license number" value={number} onChange={(e) => setNumber(e.target.value)} placeholder="e.g. DPSS-12345" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Holder full name</Label>
              <Input aria-label="Holder full name" value={holderName} onChange={(e) => setHolderName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Jurisdiction</Label>
              <Input aria-label="Jurisdiction" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} placeholder={selectedType?.jurisdiction ?? "State / region"} />
            </div>
          </div>

          {selectedType?.requires_dob && (
            <div className="space-y-1.5">
              <Label>Date of birth</Label>
              <Input aria-label="Date of birth" type="date" value={holderDob} onChange={(e) => setHolderDob(e.target.value)} />
              <p className="text-[11px] text-muted-foreground">Required for state lookup. Stored encrypted-at-rest.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Issued</Label>
              <Input aria-label="Issued" type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Expires</Label>
              <Input aria-label="Expires" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <Label className="text-sm">Awaiting hard card</Label>
              <p className="text-[11px] text-muted-foreground">
                I have completed training & passed exam but my physical card hasn't arrived yet.
              </p>
            </div>
            <Switch checked={awaitingHardCard} onCheckedChange={setAwaitingHardCard} />
          </div>

          <div className="space-y-1.5">
            <Label>Upload documents</Label>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
                <Upload className="h-4 w-4" /> Choose files
              </Button>
              <input aria-label="Upload documents"
                ref={fileRef}
                type="file"
                multiple
                accept="image/*,application/pdf"
                hidden
                onChange={(e) => {
                  setFiles(Array.from(e.target.files ?? []));
                  e.target.value = "";
                }}
              />
              <span className="text-xs text-muted-foreground">{files.length} selected</span>
            </div>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <FileText className="h-3 w-3" /> {f.name} ({Math.round(f.size / 1024)} KB)
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? "Submitting…" : "Submit for review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
